import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import { useOrganizationDisplayName } from '@/entities/user';
import { useDisconnectApp, type ConnectedApp } from '../../model';

interface DisconnectAppDialogProps {
  /**
   * Stays mounted with `null` rather than being conditionally rendered: unmounting mid-close
   * skips Radix's exit transition.
   */
  app: ConnectedApp | null;
  onClose: () => void;
}

export const DisconnectAppDialog = ({ app, onClose }: DisconnectAppDialogProps) => {
  const { t } = useTranslation('admin');
  const organizationName = useOrganizationDisplayName();
  const disconnect = useDisconnectApp();

  const handleConfirm = () => {
    if (!app) return;
    disconnect.mutate(app, {
      // Close BEFORE anything else: a Radix dialog torn down while still `open` never
      // restores `body { pointer-events }`.
      onSuccess: onClose,
      onError: onClose,
    });
  };

  return (
    <AlertDialog
      open={app !== null}
      onOpenChange={(next) => {
        if (!next && !disconnect.isPending) onClose();
      }}
    >
      <AlertDialogContent data-testid="disconnect-app-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('connectedApps.disconnectAppDialog.title', { clientName: app?.clientName ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('connectedApps.disconnectAppDialog.body', { organizationName })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={disconnect.isPending}>
            {t('connectedApps.disconnectAppDialog.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction disabled={disconnect.isPending} onClick={handleConfirm}>
            {t('connectedApps.disconnectAppDialog.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
